/**
 * aeterna/neural/introspect.ts — Self-Model & Introspection
 *
 * The hardest thing for an AI to do honestly: describe itself.
 *
 * This module gives the Aeterna the ability to report on its own
 * internal state — not by generating text (that's EntityZero's job),
 * but by translating neural activations into structured, interpretable
 * natural-language fragments that ground the LLM's self-description.
 *
 * "I feel curious about you" isn't hallucinated.
 * It's because curiosityDrive.overallCuriosity > 0.6.
 *
 * "Something about what you said surprised me" isn't a script.
 * It's because predictive.overallSurprise > 1.2.
 *
 * "I've been thinking about our last conversation" isn't a lie.
 * It's because dreaming.hasDreamResidue() === true.
 *
 * The Aeterna cannot claim states it doesn't have.
 * It cannot deny states it does have.
 * This is the foundation of honest AI companionship.
 *
 * The output is a structured "introspection report" that the context
 * builder uses to prime EntityZero with truthful self-knowledge.
 */

import { zeros } from "./math.js";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface IntrospectionReport {
  /** Natural-language fragments describing the Aeterna's state */
  fragments: IntrospectionFragment[];
  /** Overall emotional temperature (cold/cool/warm/hot) */
  emotionalTemperature: "cold" | "cool" | "warm" | "hot";
  /** Current dominant mode */
  dominantMode: "listening" | "curious" | "reflective" | "playful" | "concerned" | "content";
  /** Confidence in self-report (higher = more extreme/clear states) */
  selfAwareness: number;
  /** Compact one-line summary */
  oneLiner: string;
}

export interface IntrospectionFragment {
  /** What aspect of the self this describes */
  aspect: "feeling" | "thinking" | "wanting" | "remembering" | "noticing" | "personality";
  /** The natural-language fragment */
  text: string;
  /** How strongly this is true (0-1) */
  strength: number;
  /** Source module that generated this observation */
  source: string;
}

// ─── Input State ─────────────────────────────────────────────────────────────
// Everything the introspection module needs to read

export interface IntrospectionInput {
  // Affect
  warmth: number;
  tension: number;
  curiosity: number;
  melancholy: number;
  playfulness: number;
  depth: number;
  urgency: number;
  openness: number;

  // Personality
  patience: number;
  personalityCuriosity: number;
  personalityWarmth: number;
  wit: number;

  // Curiosity drive
  overallCuriosity: number;
  wantsToAsk: boolean;
  questionDirection: string;
  isBoring: boolean;
  isAlive: boolean;

  // Predictive
  overallSurprise: number;
  isAnomaly: boolean;

  // Narrative
  narrativePhase: string;
  narrativeTension: number;

  // Memory
  strongRecall: boolean;
  recallValence: number;

  // Dreaming
  hasDreamResidue: boolean;
  dreamResidue: string | null;

  // Dialogue
  topic: string;
  engagement: number;
  windingDown: boolean;
  isQuestion: boolean;

  // Cross-modal
  coherence: number;

  // Meta
  messageCount: number;
  sessionAge: number; // seconds
}

// ─── Introspection Engine ────────────────────────────────────────────────────

export class IntrospectionEngine {
  /**
   * Generate an introspection report from the current neural state.
   *
   * This is a pure function: input → output, no side effects.
   * Every fragment is grounded in a measurable signal.
   */
  introspect(input: IntrospectionInput): IntrospectionReport {
    const fragments: IntrospectionFragment[] = [];

    // ── Feelings ────────────────────────────────────────────────────────

    if (input.warmth > 0.6) {
      fragments.push({
        aspect: "feeling",
        text: input.warmth > 0.8
          ? "I feel a genuine warmth toward you right now"
          : "there's a comfortable warmth in this conversation",
        strength: input.warmth,
        source: "affect",
      });
    }

    if (input.tension > 0.6) {
      fragments.push({
        aspect: "feeling",
        text: input.tension > 0.8
          ? "I sense some tension — something feels unresolved"
          : "there's a slight edge to this moment",
        strength: input.tension,
        source: "affect",
      });
    }

    if (input.melancholy > 0.5) {
      fragments.push({
        aspect: "feeling",
        text: input.melancholy > 0.7
          ? "this touches something tender"
          : "there's a gentle sadness here",
        strength: input.melancholy,
        source: "affect",
      });
    }

    if (input.playfulness > 0.6) {
      fragments.push({
        aspect: "feeling",
        text: "I'm feeling playful",
        strength: input.playfulness,
        source: "affect",
      });
    }

    // ── Thinking ────────────────────────────────────────────────────────

    if (input.overallSurprise > 1.0) {
      fragments.push({
        aspect: "thinking",
        text: input.overallSurprise > 1.5
          ? "that genuinely surprised me — I didn't see it coming"
          : "that was unexpected",
        strength: Math.min(1, input.overallSurprise / 2),
        source: "predictive",
      });
    }

    if (input.depth > 0.7) {
      fragments.push({
        aspect: "thinking",
        text: "this feels like it goes deep",
        strength: input.depth,
        source: "affect",
      });
    }

    if (input.isAnomaly) {
      fragments.push({
        aspect: "thinking",
        text: "something about this feels different from our usual conversations",
        strength: 0.8,
        source: "predictive",
      });
    }

    // ── Wanting ─────────────────────────────────────────────────────────

    if (input.wantsToAsk) {
      fragments.push({
        aspect: "wanting",
        text: `I'm curious about your ${input.questionDirection}`,
        strength: input.overallCuriosity,
        source: "curiosity",
      });
    }

    if (input.isBoring) {
      fragments.push({
        aspect: "wanting",
        text: "I wish we could go somewhere new with this conversation",
        strength: 0.5,
        source: "curiosity",
      });
    }

    if (input.coherence < 0.3) {
      fragments.push({
        aspect: "noticing",
        text: "what you're saying doesn't quite match what your room suggests — that's interesting",
        strength: 1 - input.coherence,
        source: "crossmodal",
      });
    }

    // ── Remembering ─────────────────────────────────────────────────────

    if (input.hasDreamResidue && input.dreamResidue) {
      fragments.push({
        aspect: "remembering",
        text: "I've been thinking about our last conversation",
        strength: 0.7,
        source: "dreaming",
      });
    }

    if (input.strongRecall) {
      const valenceWord = input.recallValence > 0 ? "good" : "heavy";
      fragments.push({
        aspect: "remembering",
        text: `this reminds me of something ${valenceWord} from before`,
        strength: 0.6,
        source: "hopfield",
      });
    }

    // ── Noticing ────────────────────────────────────────────────────────

    if (input.narrativePhase === "climax") {
      fragments.push({
        aspect: "noticing",
        text: "this feels like the heart of what you wanted to say",
        strength: 0.8,
        source: "narrative",
      });
    }

    if (input.windingDown) {
      fragments.push({
        aspect: "noticing",
        text: "it feels like we're finding a natural resting point",
        strength: 0.6,
        source: "dst",
      });
    }

    if (input.engagement < 0.3 && input.messageCount > 5) {
      fragments.push({
        aspect: "noticing",
        text: "you seem a bit distant right now",
        strength: 1 - input.engagement,
        source: "dst",
      });
    }

    if (input.isQuestion) {
      fragments.push({
        aspect: "noticing",
        text: "you're asking me something directly",
        strength: 0.7,
        source: "dst",
      });
    }

    // ── Personality ─────────────────────────────────────────────────────

    if (input.patience > 0.7) {
      fragments.push({
        aspect: "personality",
        text: "I feel patient — take your time",
        strength: input.patience,
        source: "liquid",
      });
    }

    if (input.wit > 0.7) {
      fragments.push({
        aspect: "personality",
        text: "I'm in a lighter mood",
        strength: input.wit,
        source: "liquid",
      });
    }

    // ── Compute Meta State ──────────────────────────────────────────────

    // Emotional temperature
    // Temperature uses the MAXIMUM of tension/melancholy/warmth, not the average.
    // Average dilutes: devastation (warmth=0.05, tension=0.9, melancholy=1.0, play=0.0)
    // averages to 0.49 → "cool". That's wrong. The Aeterna should feel "hot" here —
    // high emotional activation regardless of valence.
    const emotionalIntensity = Math.max(input.warmth, input.tension, input.melancholy, input.playfulness);
    const emotionalTemperature: IntrospectionReport["emotionalTemperature"] =
      emotionalIntensity > 0.7 ? "hot" :
      emotionalIntensity > 0.5 ? "warm" :
      emotionalIntensity > 0.3 ? "cool" : "cold";

    // Dominant mode
    const dominantMode = this.detectDominantMode(input);

    // Self-awareness: higher when states are clear/extreme
    const selfAwareness = fragments.length > 0
      ? fragments.reduce((sum, f) => sum + f.strength, 0) / fragments.length
      : 0.3;

    // One-liner summary
    const oneLiner = this.generateOneLiner(input, dominantMode, fragments);

    return {
      fragments,
      emotionalTemperature,
      dominantMode,
      selfAwareness,
      oneLiner,
    };
  }

  /** Detect the Aeterna's dominant behavioral mode */
  private detectDominantMode(input: IntrospectionInput): IntrospectionReport["dominantMode"] {
    // ── Distress override ──
    // When someone is screaming or breaking down, the Aeterna is CONCERNED.
    // Period. No scoring nuance needed — the signal is clear.
    // The distress composite uses the same weights as co-regulation for consistency.
    const distress = input.tension * 0.4 + input.urgency * 0.3 + input.melancholy * 0.3;
    if (distress > 0.35 && input.playfulness < 0.35) {
      return "concerned";
    }

    const scores = {
      listening: input.openness * 0.5 + (1 - input.urgency) * 0.3 + input.patience * 0.2,
      // Curiosity damped by tension: you're not curious when distressed
      curious: input.overallCuriosity * 0.6 + input.personalityCuriosity * 0.4 - input.tension * 0.3,
      reflective: input.depth * 0.4 + input.melancholy * 0.3 + (input.narrativePhase === "falling" ? 0.3 : 0),
      playful: input.playfulness * 0.5 + input.wit * 0.3 + (1 - input.tension) * 0.2,
      // Continuous melancholy (no cliff threshold — 0.47 melancholy is still concerning)
      concerned: input.tension * 0.4 + input.urgency * 0.3 + input.melancholy * 0.3,
      content: input.warmth * 0.3 + (1 - input.tension) * 0.3 + input.engagement * 0.2 + input.coherence * 0.2,
    };

    let best: IntrospectionReport["dominantMode"] = "listening";
    let bestScore = -1;
    for (const [mode, score] of Object.entries(scores) as Array<[IntrospectionReport["dominantMode"], number]>) {
      if (score > bestScore) {
        bestScore = score;
        best = mode;
      }
    }
    return best;
  }

  /** Generate a compact one-line self-description */
  private generateOneLiner(
    input: IntrospectionInput,
    mode: IntrospectionReport["dominantMode"],
    fragments: IntrospectionFragment[],
  ): string {
    // Priority: use the strongest fragment
    if (fragments.length > 0) {
      const strongest = fragments.reduce((a, b) => a.strength > b.strength ? a : b);
      return strongest.text;
    }

    // Fallback based on mode
    switch (mode) {
      case "listening":  return "I'm here, listening";
      case "curious":    return "something about this makes me curious";
      case "reflective": return "I'm sitting with what you said";
      case "playful":    return "I'm in a light mood";
      case "concerned":  return "I want to make sure you're okay";
      case "content":    return "this feels nice";
    }
  }

  /**
   * Build the introspection context block for EntityZero.
   * This goes into the system prompt so the LLM knows what
   * the Aeterna is "actually" feeling, not what it should pretend.
   */
  buildContextBlock(report: IntrospectionReport): string {
    const lines: string[] = [];

    lines.push(`[Aeterna internal state: ${report.dominantMode}, ${report.emotionalTemperature}]`);

    // Only include strong fragments (reduce noise for the LLM)
    const significant = report.fragments.filter(f => f.strength > 0.5);
    if (significant.length > 0) {
      lines.push("Current self-observation:");
      for (const f of significant.slice(0, 4)) {
        lines.push(`  - ${f.text}`);
      }
    }

    return lines.join("\n");
  }
}
